import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm, SubmitHandler } from 'react-hook-form';
import { format, addDays, startOfDay, isSameDay } from 'date-fns';
import DatePicker from 'react-datepicker';
import TireWorksHDApi from '../services/api';
import { 
  Appointment, 
  CustomerInfo, 
  VehicleInfo, 
  ServiceType, 
  TimeSlot, 
  DealershipInfo,
  TireWorksHDConfig 
} from '../types';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin: 0 0 10px 0;
    font-size: 2rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    padding: 15px;
    
    h1 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  margin: 0 10px;
  
  .step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: white;
    background: ${props => 
      props.completed ? '#28a745' : 
      props.active ? '#667eea' : '#dee2e6'};
    margin-right: 10px;
  }
  
  .step-label {
    font-weight: 500;
    color: ${props => 
      props.completed ? '#28a745' : 
      props.active ? '#667eea' : '#6c757d'};
  }

  @media (max-width: 768px) {
    margin: 0 5px;
    
    .step-number {
      width: 30px;
      height: 30px;
      font-size: 0.9rem;
    }
    
    .step-label {
      display: none;
    }
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 10px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  .react-datepicker {
    font-family: inherit;
    border-radius: 8px;
    border: 2px solid #e9ecef;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  .react-datepicker__header {
    background: #f8f9fa;
    border-bottom: 2px solid #e9ecef;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .react-datepicker__day {
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #667eea;
      color: white;
    }

    &--highlighted {
      background-color: #d1d9ff;
      color: #667eea;
      font-weight: bold;

      &:hover {
        background-color: #667eea;
        color: white;
      }
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;

      &:hover {
        background-color: transparent;
        color: #ccc;
      }
    }

    &--selected {
      background-color: #667eea;
      color: white;
      font-weight: bold;

      &:hover {
        background-color: #5a67d8;
      }
    }
  }

  .react-datepicker__day-name {
    color: #495057;
    font-weight: 600;
  }

  .react-datepicker__current-month {
    color: #495057;
    font-weight: 600;
  }

  .react-datepicker__navigation {
    top: 8px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &.error {
    border-color: #dc3545;
  }
`;


const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div<{ selected: boolean; expanded?: boolean }>`
  padding: 12px 15px;
  border: 2px solid ${props => props.selected ? '#667eea' : '#e9ecef'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f8f9ff' : 'white'};
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  h4 {
    margin: 0 0 8px 0;
    color: #495057;
    font-size: 1rem;
  }

  .description {
    margin: 8px 0;
    color: #6c757d;
    font-size: 0.85rem;
    line-height: 1.3;
    overflow: hidden;
    transition: all 0.3s ease-out;
  }

  .service-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
    color: #667eea;
    font-weight: 500;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid #e9ecef;
  }

  .details-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #667eea;
    font-weight: 600;
    font-size: 0.85rem;
    margin-top: 8px;
    cursor: pointer;
    user-select: none;
    transition: color 0.2s ease;

    &:hover {
      color: #5a67d8;
    }

    .chevron {
      margin-left: 4px;
      transition: transform 0.3s ease;
      transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    }
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    
    h4 {
      font-size: 0.95rem;
    }
    
    .description {
      font-size: 0.8rem;
    }
    
    .service-meta {
      font-size: 0.8rem;
    }
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TimeSlotButton = styled.button<{ selected: boolean; available: boolean }>`
  padding: 12px 8px;
  border: 2px solid ${props => 
    !props.available ? '#dee2e6' :
    props.selected ? '#667eea' : '#e9ecef'};
  border-radius: 8px;
  background: ${props => 
    !props.available ? '#f8f9fa' :
    props.selected ? '#667eea' : 'white'};
  color: ${props => 
    !props.available ? '#6c757d' :
    props.selected ? 'white' : '#495057'};
  cursor: ${props => props.available ? 'pointer' : 'not-allowed'};
  font-weight: 500;
  transition: all 0.3s ease;
  opacity: ${props => props.available ? 1 : 0.5};

  &:hover {
    ${props => props.available && `
      transform: translateY(-2px);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    `}
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  padding: 12px 30px;
  border: 2px solid ${props => 
    props.variant === 'outline' ? '#667eea' : 
    props.variant === 'secondary' ? '#6c757d' : '#667eea'};
  border-radius: 8px;
  background: ${props => 
    props.variant === 'outline' ? 'transparent' : 
    props.variant === 'secondary' ? '#6c757d' : '#667eea'};
  color: ${props => 
    props.variant === 'outline' ? '#667eea' : 'white'};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    background: ${props => 
      props.variant === 'outline' ? '#667eea' : 
      props.variant === 'secondary' ? '#5a6268' : '#5a67d8'};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
    margin-bottom: 10px;
    width: 100%;
    margin-right: 0;
  }
`;



const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 5px;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
  text-align: center;

  h3 {
    margin: 0 0 10px 0;
  }

  p {
    margin: 5px 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e9ecef;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface AppointmentSchedulerProps {
  config: TireWorksHDConfig;
  widgetConfig?: {
    dealer: {
      id: string;
      name: string;
      useThirdPartyIntegration: boolean;
      thirdPartyProvider?: string;
      customization: {
        primaryColor: string;
        fontFamily: string;
      };
    };
    locations: any[];
    thirdPartyCredentials?: {
      apiEndpoint: string;
      locationId: string;
      applicationUID: string;
    };
  };
  selectedLocation?: {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phone?: string;
    email?: string;
  };
}

interface FormData extends CustomerInfo, VehicleInfo {
  notes: string;
}

const AppointmentScheduler: React.FC<AppointmentSchedulerProps> = ({ config, widgetConfig, selectedLocation }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [dealership, setDealership] = useState<DealershipInfo | null>(null);
  const [selectedServices, setSelectedServices] = useState<ServiceType[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedCalendar] = useState<{ calendarId: string; calendarName: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [appointmentCreated, setAppointmentCreated] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [dateRangeExtensions, setDateRangeExtensions] = useState(0);
  const [api] = useState(() => new TireWorksHDApi(config));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_manualServices, setManualServices] = useState<ServiceType[]>([]);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  useEffect(() => {
    console.log('Initial load - widgetConfig:', widgetConfig, 'selectedLocation:', selectedLocation);
    if (widgetConfig) {
      loadDealershipInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetConfig]);

  // Reload services when location changes (for manual services)
  useEffect(() => {
    console.log('Location changed - selectedLocation:', selectedLocation, 'widgetConfig:', widgetConfig);
    if (widgetConfig && !widgetConfig.dealer.useThirdPartyIntegration && selectedLocation) {
      console.log('Reloading services for location change');
      loadDealershipInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  useEffect(() => {
    scrollToTop();
  }, [currentStep, appointmentCreated]);

  useEffect(() => {
    if (selectedServices.length > 0) {
      loadAvailableDates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices]);

  useEffect(() => {
    if (selectedDate && selectedServices.length > 0) {
      loadAvailableTimeSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedServices]);

  const loadDealershipInfo = async () => {
    setLoading(true);
    
    // If we have widget config and it's not using third-party integration
    if (widgetConfig && !widgetConfig.dealer.useThirdPartyIntegration) {
      // Load services from our API and create dealership info
      const services = await loadManualServices();
      
      const mockDealership: DealershipInfo = {
        id: widgetConfig.dealer.id,
        name: widgetConfig.dealer.name,
        address: selectedLocation?.address || '',
        city: selectedLocation?.city || '',
        state: selectedLocation?.state || '',
        zipCode: selectedLocation?.zipCode || '',
        phone: selectedLocation?.phone || '',
        email: selectedLocation?.email || '',
        businessHours: {
          // Default business hours
          monday: { open: '08:00', close: '18:00' },
          tuesday: { open: '08:00', close: '18:00' },
          wednesday: { open: '08:00', close: '18:00' },
          thursday: { open: '08:00', close: '18:00' },
          friday: { open: '08:00', close: '18:00' },
          saturday: { open: '08:00', close: '17:00' },
          sunday: { closed: true, open: '', close: '' }
        },
        services: services
      };
      setDealership(mockDealership);
    } else {
      // Use third-party API (existing logic)
      const response = await api.getDealershipInfo();
      if (response.success && response.data) {
        const dealershipData = response.data;
        // Apply dealer-configured service visibility filter
        if (config.allowedServices && config.allowedServices.length > 0) {
          const allowSet = new Set(config.allowedServices);
          dealershipData.services = dealershipData.services.filter(s => allowSet.has(s.id));
        }
        setDealership(dealershipData);
      }
    }
    
    setLoading(false);
  };

  const loadManualServices = async (): Promise<ServiceType[]> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      const locationParam = selectedLocation ? `?locationId=${selectedLocation.id}` : '';
      const response = await fetch(`${apiUrl}/embed/services/${config.embedToken}${locationParam}`);
      const data = await response.json();
      
      console.log('Manual services API response:', data); // Debug log
      
      if (data.success && data.data && data.data.services) {
        // Convert manual services to ServiceType format
        const services: ServiceType[] = data.data.services.map((service: any) => ({
          id: service.id,
          name: service.name,
          description: service.description || '',
          estimatedDuration: service.durationMinutes || 60,
          price: service.price,
          category: 'maintenance' as const, // Default category for manual services
          twhdPackageCode: service.twhdPackageCode || null
        }));
        
        console.log('Converted services:', services); // Debug log
        setManualServices(services);
        return services;
      } else {
        console.log('No services found in API response'); // Debug log
        setManualServices([]);
        return [];
      }
    } catch (error) {
      console.error('Error loading manual services:', error);
      setManualServices([]);
      return [];
    }
  };

  const loadAvailableDates = async (startFromDay: number = 1, numberOfDays: number = 10) => {
    if (selectedServices.length === 0) return;

    setLoading(true);
    const dates: Date[] = [...availableDates]; // Keep existing dates
    const today = startOfDay(new Date());
    const serviceIds = selectedServices.map(s => s.id);

    // Check if we should use standalone availability checking
    if (widgetConfig && !widgetConfig.dealer.useThirdPartyIntegration) {
      await loadStandaloneAvailableDates(startFromDay, numberOfDays, dates);
    } else {
      // Use third-party API (existing logic)
      for (let i = startFromDay; i < startFromDay + numberOfDays; i++) {
        const date = addDays(today, i);
        const dateStr = format(date, 'yyyy-MM-dd');
        const response = await api.getAvailableTimeSlots(dateStr, serviceIds);
        if (response.success && response.data && response.data.some(slot => slot.available)) {
          dates.push(date);
        }
      }
    }

    setAvailableDates(dates);
    setLoading(false);
  };

  const loadStandaloneAvailableDates = async (startFromDay: number, numberOfDays: number, dates: Date[]) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      const serviceIds = selectedServices.map(s => s.id).join(',');
      const locationParam = selectedLocation ? `&locationId=${selectedLocation.id}` : '';
      const today = startOfDay(new Date());
      const startDate = format(addDays(today, startFromDay), 'yyyy-MM-dd');
      
      const response = await fetch(`${apiUrl}/embed/availability/${config.embedToken}?serviceIds=${serviceIds}&startDate=${startDate}&days=${numberOfDays}${locationParam}`);
      const data = await response.json();
      
      console.log('Standalone availability response:', data); // Debug log
      
      if (data.success && data.data && data.data.availableDates) {
        data.data.availableDates.forEach((dateStr: string) => {
          const date = new Date(dateStr);
          // Only add if not already in the array
          if (!dates.some(d => format(d, 'yyyy-MM-dd') === dateStr)) {
            dates.push(date);
          }
        });
      }
    } catch (error) {
      console.error('Error loading standalone availability:', error);
    }
  };

  const loadMoreDates = async () => {
    if (dateRangeExtensions >= 2) return; // Limit to 2 extensions (30 days total)
    
    const startFromDay = 10 + (dateRangeExtensions * 10) + 1;
    await loadAvailableDates(startFromDay, 10);
    setDateRangeExtensions(prev => prev + 1);
  };

  const loadAvailableTimeSlots = async () => {
    if (!selectedDate || selectedServices.length === 0) return;
    
    setLoading(true);
    const serviceIds = selectedServices.map(s => s.id);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    // Check if we should use standalone time slot checking
    if (widgetConfig && !widgetConfig.dealer.useThirdPartyIntegration) {
      await loadStandaloneTimeSlots(dateStr, serviceIds);
    } else {
      // Use third-party API (existing logic)
      const response = await api.getAvailableTimeSlots(dateStr, serviceIds);
      if (response.success && response.data) {
        // Ensure all time slots have a calendar ID
        const timeSlots = response.data.map(slot => ({
          ...slot,
          calendarId: slot.calendarId || selectedCalendar?.calendarId || ''
        }));
        setAvailableTimeSlots(timeSlots);
      }
    }
    
    setLoading(false);
  };

  const loadStandaloneTimeSlots = async (dateStr: string, serviceIds: string[]) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      const serviceIdsParam = serviceIds.join(',');
      const locationParam = selectedLocation ? `&locationId=${selectedLocation.id}` : '';
      
      const response = await fetch(`${apiUrl}/embed/timeslots/${config.embedToken}?serviceIds=${serviceIdsParam}&date=${dateStr}${locationParam}`);
      const data = await response.json();
      
      console.log('Standalone time slots response:', data); // Debug log
      
      if (data.success && data.data) {
        const timeSlots = data.data.map((slot: any) => ({
          id: slot.id,
          date: slot.date,
          time: slot.time,
          available: slot.available,
          duration: slot.duration,
          calendarId: slot.bayId, // Use bayId as calendarId for consistency
          bayId: slot.bayId,
          bayName: slot.bayName
        }));
        setAvailableTimeSlots(timeSlots);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('Error loading standalone time slots:', error);
      setAvailableTimeSlots([]);
    }
  };

  const handleServiceToggle = (service: ServiceType, e: React.MouseEvent) => {
    // Check if the click target is the details link
    const target = e.target as HTMLElement;
    if (target.classList.contains('details-link')) {
      e.stopPropagation(); // Prevent service selection when clicking details
      setExpandedDescriptions(prev => {
        const newSet = new Set(prev);
        if (newSet.has(service.id)) {
          newSet.delete(service.id);
        } else {
          newSet.add(service.id);
        }
        return newSet;
      });
      return;
    }

    setSelectedServices(prev => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
    setSelectedTimeSlot(null); // Reset time slot when services change
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Reset time slot when date changes
  };

  const isDateAvailable = (date: Date): boolean => {
    return availableDates.some(availableDate => isSameDay(availableDate, date));
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    if (timeSlot.available) {
      // Ensure the time slot has a calendar ID
      const updatedTimeSlot = {
        ...timeSlot,
        calendarId: timeSlot.calendarId || selectedCalendar?.calendarId || ''
      };
      setSelectedTimeSlot(updatedTimeSlot);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!selectedTimeSlot || selectedServices.length === 0) return;

    setLoading(true);

    // Check if we should use standalone appointment creation
    if (widgetConfig && !widgetConfig.dealer.useThirdPartyIntegration) {
      await createStandaloneAppointment(data);
    } else {
      // Use third-party API (existing logic)
      const appointment: Appointment = {
        customer: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode
        },
        vehicle: {
          year: data.year,
          make: data.make,
          model: data.model,
          mileage: data.mileage,
          licensePlate: data.licensePlate
        },
        services: selectedServices,
        timeSlot: selectedTimeSlot,
        notes: data.notes,
        status: 'pending',
        dealershipId: config.dealershipId
      };

      const response = await api.createAppointment(appointment);
      
      if (response.success && response.data) {
        setAppointmentCreated(response.data.appointmentId);
        setCurrentStep(5);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    setLoading(false);
  };

  const createStandaloneAppointment = async (data: FormData) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      
      // Get the bay ID from the selected time slot
      const bayId = (selectedTimeSlot as any)?.bayId;
      if (!bayId) {
        console.error('No bay ID found in selected time slot');
        return;
      }

      // Calculate total duration
      const totalDuration = selectedServices.reduce((sum, service) => sum + service.estimatedDuration, 0);

      // Convert 24-hour time to 12-hour format for calendar compatibility
      const convertTo12HourFormat = (time24: string): string => {
        const [hours, minutes] = time24.split(':').map(Number);
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return minutes > 0 ? `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}` : `${displayHours}${ampm}`;
      };

      const appointmentData = {
        dealerId: widgetConfig?.dealer.id,
        locationId: selectedLocation?.id,
        bayId: bayId,
        date: format(selectedDate!, 'yyyy-MM-dd'),
        startTime: convertTo12HourFormat(selectedTimeSlot!.time),
        duration: totalDuration,
        customer: {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          email: data.email
        },
        vehicle: {
          year: data.year,
          make: data.make,
          model: data.model,
          trim: data.mileage // Using mileage field as trim for now
        },
        services: selectedServices.map(service => ({
          serviceId: service.id,
          serviceName: service.name,
          duration: service.estimatedDuration,
          price: service.price
        })),
        notes: data.notes
      };

      console.log('Creating standalone appointment:', appointmentData); // Debug log

      const response = await fetch(`${apiUrl}/embed/appointments/${config.embedToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });

      const result = await response.json();
      
      console.log('Standalone appointment response:', result); // Debug log

      if (result.success && result.data) {
        setAppointmentCreated(result.data.id);
        setCurrentStep(5);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.error('Failed to create standalone appointment:', result.error);
        // Could show error message to user here
      }
    } catch (error) {
      console.error('Error creating standalone appointment:', error);
      // Could show error message to user here
    }
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      scrollToTop();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  const canProceedToStep = (step: number): boolean => {
    switch (step) {
      case 2:
        return selectedServices.length > 0;
      case 3:
        return selectedDate !== null;
      case 4:
        return selectedTimeSlot !== null;
      default:
        return true;
    }
  };


  if (loading && !dealership) {
    return (
      <Container>
        <LoadingSpinner>
          <div className="spinner"></div>
        </LoadingSpinner>
      </Container>
    );
  }

  if (!dealership) {
    return (
      <Container>
        <Card>
          <h2>Unable to load dealership information</h2>
          <p>Please try again later or contact support.</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1>Schedule Your Appointment</h1>
        <p>{dealership.name} - {dealership.city}, {dealership.state}</p>
      </Header>



      {appointmentCreated ? (
        <Card>
          <SuccessMessage>
            <h3>🎉 Appointment Scheduled Successfully!</h3>
            <p><strong>Appointment ID:</strong> {appointmentCreated}</p>
            <p><strong>Date:</strong> {selectedTimeSlot && format(new Date(`${selectedTimeSlot.date}T${selectedTimeSlot.time}`), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}</p>
            <p><strong>Services:</strong> {selectedServices.map(s => s.name).join(', ')}</p>
            <p>You will receive a confirmation email shortly. Please arrive 15 minutes early for your appointment.</p>
          </SuccessMessage>
        </Card>
      ) : (
        <>
          <StepIndicator>
            <Step active={currentStep === 1} completed={currentStep > 1}>
              <div className="step-number">1</div>
              <div className="step-label">Services</div>
            </Step>
            <Step active={currentStep === 2} completed={currentStep > 2}>
              <div className="step-number">2</div>
              <div className="step-label">Date</div>
            </Step>
            <Step active={currentStep === 3} completed={currentStep > 3}>
              <div className="step-number">3</div>
              <div className="step-label">Time</div>
            </Step>
            <Step active={currentStep === 4} completed={currentStep > 4}>
              <div className="step-number">4</div>
              <div className="step-label">Details</div>
            </Step>
          </StepIndicator>

          {currentStep === 1 && (
            <Card>
              <h2>Select Services</h2>
              {selectedServices.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <Button 
                    onClick={nextStep}
                    disabled={!canProceedToStep(2)}
                  >
                    Continue with {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
              <ServiceGrid>
                {dealership.services.map(service => (
                  <ServiceCard
                    key={service.id}
                    selected={selectedServices.some(s => s.id === service.id)}
                    expanded={expandedDescriptions.has(service.id)}
                    onClick={(e) => handleServiceToggle(service, e)}
                  >
                    <h4>{service.name}</h4>
                    <div className="service-meta">
                      <span>{service.estimatedDuration} min</span>
                      {service.price && <span>${service.price}</span>}
                    </div>
                    <div 
                      className="description"
                      style={{ 
                        maxHeight: expandedDescriptions.has(service.id) ? '200px' : '0',
                        opacity: expandedDescriptions.has(service.id) ? 1 : 0
                      }}
                    >
                      {service.description}
                    </div>
                    <div 
                      className="details-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDescriptions(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(service.id)) {
                            newSet.delete(service.id);
                          } else {
                            newSet.add(service.id);
                          }
                          return newSet;
                        });
                      }}
                    >
                      {expandedDescriptions.has(service.id) ? 'Show Less' : 'Show Details'}
                      <span className="chevron">▼</span>
                    </div>
                  </ServiceCard>
                ))}
              </ServiceGrid>
              {selectedServices.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <Button 
                    onClick={nextStep}
                    disabled={!canProceedToStep(2)}
                  >
                    Continue with {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </Card>
          )}

          {currentStep === 2 && (
            <Card>
              <h2>Select Date</h2>
              <FormGroup>
                <Label>Preferred Date</Label>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  minDate={addDays(new Date(), 1)}
                  maxDate={addDays(new Date(), 10 + (dateRangeExtensions * 10))}
                  filterDate={isDateAvailable}
                  placeholderText="Select a date..."
                  dateFormat="EEEE, MMMM d, yyyy"
                  className="form-control"
                  inline
                  highlightDates={availableDates}
                  disabled={loading || availableDates.length === 0}
                />
                {availableDates.length === 0 && !loading && (
                  <ErrorMessage>No available dates found for the selected services.</ErrorMessage>
                )}
                {dateRangeExtensions < 2 && (
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <Button 
                      variant="outline"
                      onClick={loadMoreDates}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Looking for a date in the future? Load more'}
                    </Button>
                  </div>
                )}
              </FormGroup>
              <div>
                <Button variant="outline" onClick={prevStep}>
                  Back
                </Button>
                <Button 
                  onClick={nextStep} 
                  disabled={!canProceedToStep(3)}
                >
                  Continue
                </Button>
              </div>
            </Card>
          )}

          {currentStep === 3 && (
            <Card>
              <h2>Select Time</h2>
              {loading ? (
                <LoadingSpinner>
                  <div className="spinner"></div>
                </LoadingSpinner>
              ) : (
                <>
                  <p>Available time slots for {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : ''}</p>
                  <TimeSlotGrid>
                    {availableTimeSlots.map(timeSlot => (
                      <TimeSlotButton
                        key={timeSlot.id}
                        selected={selectedTimeSlot?.id === timeSlot.id}
                        available={timeSlot.available}
                        onClick={() => handleTimeSlotSelect(timeSlot)}
                        disabled={!timeSlot.available}
                      >
                        {format(new Date(`2000-01-01T${timeSlot.time}`), 'h:mm a')}
                      </TimeSlotButton>
                    ))}
                  </TimeSlotGrid>
                </>
              )}
              <div>
                <Button variant="outline" onClick={prevStep}>
                  Back
                </Button>
                <Button 
                  onClick={nextStep} 
                  disabled={!canProceedToStep(4)}
                >
                  Continue
                </Button>
              </div>
            </Card>
          )}

          {currentStep === 4 && (
            <Card>
              <h2>Contact & Vehicle Information</h2>
              <form onSubmit={handleSubmit(onSubmit)}>
                <h3>Contact Information</h3>
                <FormRow>
                  <FormGroup>
                    <Label>First Name *</Label>
                    <Input
                      {...register('firstName', { required: 'First name is required' })}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name *</Label>
                    <Input
                      {...register('lastName', { required: 'Last name is required' })}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Please enter a valid email'
                        }
                      })}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      {...register('phone', { required: 'Phone number is required' })}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <h3>Vehicle Information</h3>
                <FormRow>
                  <FormGroup>
                    <Label>Year *</Label>
                    <Input
                      {...register('year', { required: 'Vehicle year is required' })}
                      className={errors.year ? 'error' : ''}
                    />
                    {errors.year && <ErrorMessage>{errors.year.message}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Make *</Label>
                    <Input
                      {...register('make', { required: 'Vehicle make is required' })}
                      className={errors.make ? 'error' : ''}
                    />
                    {errors.make && <ErrorMessage>{errors.make.message}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Model *</Label>
                    <Input
                      {...register('model', { required: 'Vehicle model is required' })}
                      className={errors.model ? 'error' : ''}
                    />
                    {errors.model && <ErrorMessage>{errors.model.message}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Mileage</Label>
                    <Input
                      type="number"
                      {...register('mileage')}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Additional Notes</Label>
                  <TextArea
                    {...register('notes')}
                    placeholder="Any specific concerns or requests..."
                  />
                </FormGroup>

                <div>
                  <Button variant="outline" type="button" onClick={prevStep}>
                    Back
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Scheduling...' : 'Schedule Appointment'}
                  </Button>
                </div>
              </form>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default AppointmentScheduler;